import React, { FC, useCallback, useMemo } from 'react';
import { useAppContext, useReportsContext } from 'QuoteApp/services/providers';
import {
  GetDetailsResponseType,
  ThirdPartyReports,
} from 'QuoteApp/services/endpoints/GetDetails';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import { Report } from '../Report';
import { reportEvent } from 'Shared/analytics';
import { useReportRunner } from '../useReportRunner';
import { ReportStatus } from '../types';
import { getReportStatus, haveAllRequiredReportsRun } from '../utils';
import { Asset, YesIcon } from '@clearcover/atlas';
import styles from './styles.module.css';

const SELECTIVE_MVR_ORDERING_ENUM = {
  ORDER: 'order',
  NO_ORDER: 'noOrder',
};

export const MvrReport: FC = () => {
  const { details, refreshDetails, refreshConfig } = useAppContext();
  const {
    reportsDisabled,
    reportInProgress,
    changeReportInProgress,
    acknowledgeDisclosure,
    reports,
  } = useReportsContext();

  const {
    runReport,
    messages,
    showRateChangeModal,
    rateChangeModalData,
    setShowRateChangeModal,
  } = useReportRunner('clue-mvr');

  const mvrStatus = useMemo(() => {
    if (reportInProgress === ThirdPartyReports.All) {
      return ReportStatus.Pending;
    } else if (reports.clueMvr) {
      return getReportStatus(reports.clueMvr.status);
    }
    return ReportStatus.Disabled;
  }, [reports.clueMvr, reportInProgress]);

  const canRunMvr = useMemo<boolean>(() => {
    if (reportInProgress) {
      return false;
    } else if (!haveAllRequiredReportsRun(ThirdPartyReports.All, reports)) {
      return false;
    }
    return reports.clueMvr?.status === 'runnable';
  }, [reportInProgress, reports]);

  const runMvr = useCallback(
    async (newDetails: GetDetailsResponseType) => {
      try {
        changeReportInProgress(ThirdPartyReports.All);
        await acknowledgeDisclosure(Disclosures.Reports);

        const newMvrReport = newDetails.commands.reports.find(
          r => r.reportName === ThirdPartyReports.All
        );

        if (newMvrReport?.status !== 'runnable') {
          changeReportInProgress(null);
          return;
        }

        await runReport(newMvrReport, true);
        reportEvent('clue-mvr-success');
        changeReportInProgress(null);
      } catch (e) {
        // If report failed, it might be because product toggled and Application now requires
        // additional data to fill. Thus, refreshing details and config.
        refreshConfig();
        refreshDetails();
        changeReportInProgress(null);
      }
    },
    [
      changeReportInProgress,
      acknowledgeDisclosure,
      runReport,
      refreshDetails,
      refreshConfig,
    ]
  );

  const needsToRun =
    details.drivers[details.principalInsuredId].selectiveMvrOrdering.value ===
    SELECTIVE_MVR_ORDERING_ENUM.ORDER;

  return reports.clueMvr ? (
    <Report
      displayName="Motor Vehicle Record & Vehicle History Report"
      messages={messages}
      enabled={!reportsDisabled && canRunMvr}
      onClick={() => {
        runMvr(details);
      }}
      status={mvrStatus}
      type="clue-mvr"
      buttonLabel={needsToRun ? 'Run Reports' : 'Run History Only'}
      preRunLabel={
        needsToRun ? (
          ''
        ) : (
          <div className={styles.preRunLabel}>
            <Asset
              icon={YesIcon}
              height={14}
              width={14}
              description="success icon"
            />
            <div>MVR not needed for this application</div>
          </div>
        )
      }
      showRateChangeModal={showRateChangeModal}
      rateChangeModalData={rateChangeModalData || undefined}
      setShowRateChangeModal={setShowRateChangeModal}
    />
  ) : null;
};
